import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, ChangeEvent, useCallback } from 'react';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Grid from '@mui/system/Unstable_Grid';
import { Box, Card, Stack, Divider, CardHeader, CardContent } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useParams, useRouter } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { rootApi } from 'src/services/rootApi';
import { useAppDispatch } from 'src/store/store';
import { PageOptions } from 'src/services/api.types';
import { JobFilters } from 'src/services/jobs/jobs.types';
import { isOrganisationTenant } from 'src/services/auth/auth.utils';
import { openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';
import { ContactLinkEntityType } from 'src/services/contacts/contacts.types';
import {
  useGetCompanyByIdQuery,
  useGetCompanyJobsQuery,
  useGetCompanyActivitiesQuery,
  useGetCompanyApplicationsQuery,
  useGetCompanyContactLinksQuery,
  useUpdateLocalCompanyMutation
} from 'src/services/connections/connections.service';

import Label from 'src/components/label';
import EntityAvatar from 'src/components/entity-avatar';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import ApplicationsTable from 'src/components/custom-tables/applications-table/applications-table';

import ActivityFeed from 'src/sections/activity/card/activity-feed';
import { LinkedContactsCard } from 'src/sections/contacts/components/linked-contacts-card';
import { CompanyProfileSection } from 'src/sections/companies/detail/sections/profile-section';

import { TenantType } from 'src/types/enums';

import AccountOwnerCard from './sections/account-owner-card';
import CompanyLinksCard from './sections/company-links-card';
import CompanyDocumentsView from './sections/company-documents';
import CompanyOrgChart from './sections/org-chart/company-org-chart';
import { CompanyDetailSection } from './sections/detail-section';
import PrimaryContactCard from './sections/primary-contact-card';
import { CompanyLabelsCard } from './sections/company-labels-card';
import ContactLinksTable from '../table/contact-links/contact-links-table';
import { CompanyProfileTypeIcon } from '../icons/company-profile-type.icon';
import ConnectionJobsTable from '../../connections/detail/connection-jobs-table';

// ----------------------------------------------------------------------

export const JOB_PUBLISH_OPTIONS = [
  {
    value: 'published',
    label: 'Published',
  },
  {
    value: 'draft',
    label: 'Draft',
  },
];

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

enum CompaniesTab {
  DETAIL = 'detail',
  PROFILE = 'profile',
  JOBS = 'jobs',
  APPLICATIONS = 'applications',
  CONTACTS = 'contacts',
  ORG_CHART = 'org_chart',
  DOCUMENTS = 'documents',
}

export default function MyCompaniesDetailView({ overrideConnectionId }: { overrideConnectionId?: string } = {}) {
  const showTable = useBoolean();

  const { t } = useTranslate();

  // `overrideConnectionId` lets the record overlay (JT1-1529) embed this view without a route param.
  const { connection_id: connectionIdParam } = useParams();
  const connection_id = overrideConnectionId ?? connectionIdParam;

  const dispatch = useAppDispatch();

  const [jobsPageOptions, setJobsPageOptions] = useState<PageOptions>(DEFAULT_PAGE);

  const [applicationsPageOptions, setApplicationsPageOptions] = useState<PageOptions>(DEFAULT_PAGE);

  const [contactsPageOptions, setContactsPageOptions] = useState<PageOptions>(DEFAULT_PAGE);

  const [activitiesPage, setActivitiesPage] = useState({ page: 1, per_page: 10 });

  const [currentTab, setCurrentTab] = useState<CompaniesTab | undefined>();

  const [searchParams, setSearchParams] = useSearchParams();

  const router = useRouter();

  const tenantType = useOrgTenant();

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (currentTab) return;

    let defaultTab: CompaniesTab = CompaniesTab.DETAIL

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(CompaniesTab).includes(existingTab as CompaniesTab)) {
      defaultTab = existingTab as CompaniesTab;
    }

    setCurrentTab(defaultTab);

  }, [currentTab, tenantType, searchParams]);

  useEffect(() => {
    if (!tenantType) return;
    // Embedded in the record overlay (JT1-1529): never navigate the whole app away.
    if (overrideConnectionId) return;

    if (tenantType === TenantType.Candidate) {
      router.push(paths.dashboard.root);
    }
  }, [router, tenantType, overrideConnectionId]);

  const { currentData: connectionProfile } = useGetCompanyByIdQuery({
    profile_id: connection_id as string
  });

  const handleChangeTab = useCallback((_event: React.SyntheticEvent, newValue: CompaniesTab) => {
    setCurrentTab(newValue);
  }, []);

  const [filters, setFilters] = useState<Omit<JobFilters, 'page' | 'per_page'>>({
    search_query: undefined,
    job_status: undefined, // TODO
    assigned_account: undefined,
  });

  const { currentData: connectionJobs } = useGetCompanyJobsQuery(
    {
      params: { ...jobsPageOptions, ...filters },
      profile_id: connection_id as string
    },
    { skip: !connection_id }
  );

  const { currentData: connectionApplications, isLoading: isLoadingApplications } = useGetCompanyApplicationsQuery({
    params: applicationsPageOptions,
    profile_id: connection_id as string
  });

  const { currentData: contactLinks } = useGetCompanyContactLinksQuery({
    params: contactsPageOptions,
    profile_id: connection_id as string
  });

  const { data: activities } = useGetCompanyActivitiesQuery({
    profile_id: connection_id as string,
    page: activitiesPage.page,
    perPage: activitiesPage.per_page,
  }, { skip: !connection_id });

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!currentTab) return;

    // Embedded in the record overlay (JT1-1529): the tab is local state; writing it to the URL would
    // wipe the co-owned ?preview params and close the overlay. Only sync on the standalone page.
    if (overrideConnectionId) return;

    setSearchParams({ "tab": currentTab }, { replace: true })

  }, [currentTab, setSearchParams, overrideConnectionId]);

  const onChangeJobsPage = useCallback((_event: any, pageNumber: number) => {
    setJobsPageOptions((prev) => ({
      ...prev,
      page: pageNumber + 1,
    }));
  }, []);

  const onChangeJobsRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setJobsPageOptions((prev) => ({
        ...prev,
        per_page: +event.target.value,
      }));
    },
    []
  );

  const onChangeFilters = useCallback((name: keyof JobFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const onClearFilters = useCallback(() => {
    setFilters({
      search_query: undefined,
      job_status: undefined,
      assigned_account: undefined,
    });
  }, []);

  const onChangeApplicationsPage = useCallback((pageNumber: number) => {
    setApplicationsPageOptions((prev) => ({
      ...prev,
      page: pageNumber,
    }));
  }, []);

  const onChangeApplicationsRowsPerPage = useCallback((perPage: number) => {
    setApplicationsPageOptions((prev) => ({
      ...prev,
      per_page: perPage,
    }));
  }, []);

  const onRatingChange = useCallback((id: string, rating: number) => {
    dispatch(rootApi.util.invalidateTags([{ type: 'CompanyApplications', id: connection_id as string }]));
  }, [dispatch, connection_id]);

  const handleAddTask = useCallback(() => {
    dispatch(openTaskEditorSidesheet({
      companyId: connection_id,
    }))
  }, [dispatch, connection_id]);

  // Logo upload/remove — writes CompanyProfile.company_logo (JT1-1539). Candidates never reach this
  // view (they are redirected above), so the affordance is org-tenant only by construction.
  const [updateLocalCompany] = useUpdateLocalCompanyMutation();
  const canEditLogo = tenantType !== TenantType.Candidate;
  const handleLogoChange = useCallback(async (fileId: string | null) => {
    if (!connection_id) return;
    try {
      await updateLocalCompany({ profile_id: connection_id, data: { company_logo_id: fileId ?? null } }).unwrap();
    } catch {
      enqueueSnackbar(t('connections.api.update_company.default_error', { defaultValue: 'Could not update the logo' }), { variant: 'error' });
    }
  }, [updateLocalCompany, connection_id, t]);

  const renderHead = (
    <Card sx={{ flexGrow: 0, boxShadow: 'unset', pb: 1 }}>
      <CardHeader
        sx={{ pl: 0, width: '100%' }}
        title={
          <Stack direction="row" justifyContent="center" alignItems="center">
            <TruncatedTextField
              text={connectionProfile?.company_name ?? 'Loading...'}
              limit={40}
              mode='characters'
              variant="h3"
              showMoreEnabled={false}
            />

          </Stack>
        }
        avatar={
          <Box height="100%" paddingTop={1}>
            <EntityAvatar
              src={connectionProfile?.company_logo?.public_path}
              name={connectionProfile?.company_name}
              size={72}
              variant="rounded"
              editable={canEditLogo}
              outputFormat="png"
              fallbackIcon="solar:buildings-bold"
              cropTitle={t('components.entity_avatar.company_logo_title')}
              onChange={handleLogoChange}
            />
          </Box>
        }
        action={
          <Stack direction="row" spacing={.5} alignItems="center" mt={2}>

            <CompanyProfileTypeIcon type={connectionProfile?.type} />

            {
              connectionProfile?.company_type && (
                <Label variant="soft">{t(`connections.table.company_type.${connectionProfile?.company_type}`)}</Label>
              )
            }

          </Stack>
        }
      />
    </Card>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab} sx={{ pl: 2, mb: 2 }}>

      <Tab
        key={CompaniesTab.DETAIL}
        iconPosition="end"
        value={CompaniesTab.DETAIL}
        label={t('connections.detail.tabs.detail')}
      />

      <Tab
        key={CompaniesTab.PROFILE}
        iconPosition="end"
        value={CompaniesTab.PROFILE}
        label={t('connections.detail.tabs.profile')}
      />

      <Tab
        key={CompaniesTab.JOBS}
        iconPosition="end"
        value={CompaniesTab.JOBS}
        label={t('connections.detail.tabs.jobs')}
        icon={<Label variant="filled">{connectionJobs?.count ?? 0}</Label>}
      />

      {tenantType !== TenantType.Candidate && (
        <Tab
          key={CompaniesTab.APPLICATIONS}
          iconPosition="end"
          value={CompaniesTab.APPLICATIONS}
          label={t(
            `connections.detail.tabs.${isOrganisationTenant(tenantType) ? 'applications' : 'applications_candidate'
            }`
          )}
          icon={<Label variant="filled">{connectionApplications?.count ?? 0}</Label>}
        />
      )}

      <Tab
        key={CompaniesTab.CONTACTS}
        value={CompaniesTab.CONTACTS}
        iconPosition="end"
        label={t('connections.detail.tabs.contacts')}
        icon={<Label variant="filled">{contactLinks?.count ?? 0}</Label>}
      />

      <Tab
        key={CompaniesTab.ORG_CHART}
        value={CompaniesTab.ORG_CHART}
        iconPosition="end"
        label={t('connections.detail.tabs.org_chart')}
      />

      <Tab
        key={CompaniesTab.DOCUMENTS}
        value={CompaniesTab.DOCUMENTS}
        iconPosition="end"
        label={t('connections.detail.tabs.documents')}
      />

    </Tabs>
  );

  return (
    <>
      <Grid container>
        <Grid xs={12}>
          {renderHead}
        </Grid>
      </Grid>

      {renderTabs}

      <Grid container spacing={2}>
        <Grid xs={12} md={8}>
          {currentTab === CompaniesTab.DETAIL && (
            <Stack spacing={2}>
              <CompanyDetailSection companyProfileId={connection_id as string} />
              <Card>
                <CardHeader title={t('admin-connections.detail.activities')} />
                <CardContent>
                  <ActivityFeed
                    activities={activities?.results ?? []}
                    page={activitiesPage.page}
                    perPage={activitiesPage.per_page}
                    onPageChange={(_e, newPage) => setActivitiesPage({ ...activitiesPage, page: newPage })}
                    totalItems={activities?.count ?? 0}
                  />
                </CardContent>
              </Card>
            </Stack>
          )}

          {currentTab === CompaniesTab.PROFILE && (
            <CompanyProfileSection companyProfileId={connection_id as string} />
          )}

          {currentTab === CompaniesTab.JOBS && (
            <ConnectionJobsTable
              jobs={connectionJobs}
              pageOptions={jobsPageOptions}
              onChangePage={onChangeJobsPage}
              onChangeRowsPerPage={onChangeJobsRowsPerPage}
              showTable={showTable}
              filters={filters}
              onChangeFilters={onChangeFilters}
              onClearFilters={onClearFilters}
            />
          )}

          {currentTab === CompaniesTab.APPLICATIONS && (
            <ApplicationsTable
              loading={isLoadingApplications}
              tableData={connectionApplications}
              pageOptions={applicationsPageOptions}
              onChangePage={onChangeApplicationsPage}
              onChangeRowsPerPage={onChangeApplicationsRowsPerPage}
              onRatingChange={onRatingChange}
            />
          )}

          {currentTab === CompaniesTab.CONTACTS && (
            <ContactLinksTable
              profileId={connection_id as string}
            />
          )}

          {currentTab === CompaniesTab.ORG_CHART && (
            <CompanyOrgChart companyProfileId={connection_id as string} />
          )}

          {currentTab === CompaniesTab.DOCUMENTS && (
            <CompanyDocumentsView profileId={connection_id as string} />
          )}
        </Grid>

        <Grid xs={12} md={4}>
          <Stack direction="column" spacing={2}>
            <Stack direction={{ xs: "column" }} spacing={2} width="100%" justifyContent="center" alignItems="center">
              <Box width="100%" >
                <AccountOwnerCard profileId={connection_id as string} />
              </Box>

              <Box width="100%" >
                <PrimaryContactCard profileId={connection_id as string} />
              </Box>
            </Stack>

            <Divider sx={{ borderStyle: 'dashed' }} />

            <LinkedContactsCard entityId={connection_id as string} entityType={ContactLinkEntityType.COMPANY_PROFILE} enableTypeSelection />

            <CompanyLinksCard profileId={connection_id as string} />

            <CompanyLabelsCard companyProfileId={connection_id as string} />
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}
